import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getMarketingContext, pushMarketingEvent } from '../utils/marketingContext';

const CONTROL_CHARS = /[\u0000-\u001F\u007F\u2028\u2029]/g;

const ALLOWED_QUERY_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'msclkid',
  'fbc',
  'fbp',
  'mm_entry',
  'mm_lp',
  'lp_variant',
  'mm_channel',
  // Email attribution
  'eqid',
  'flow_step',
  // VSL engagement tracking fields (munged into telemetry even if not in visible URL)
  'vsl_variant',
  'hero_variant',
  'hook_variant',
] as const;

function safeValue(value: string, maxLen: number): string {
  return value
    .replace(CONTROL_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen);
}

function normalizeParam(params: URLSearchParams, snake: string, camel?: string): string | null {
  return params.get(snake) || (camel ? params.get(camel) : null);
}

function sanitizePageUrl(pathname: string, search: string, mungedParams?: Record<string, string | null | undefined>): string {
  const params = new URLSearchParams(search);
  const safeParams = new URLSearchParams();

  // Optional munging: inject persisted attribution parameters into telemetry
  // without mutating the visible URL.
  if (mungedParams) {
    for (const [key, value] of Object.entries(mungedParams)) {
      if (!value) continue;
      if (params.has(key)) continue; // never overwrite explicit querystring
      params.set(key, value);
    }
  }

  const mapped: Array<[string, string | undefined, number]> = [
    ['utm_source', 'utmSource', 200],
    ['utm_medium', 'utmMedium', 200],
    ['utm_campaign', 'utmCampaign', 200],
    ['utm_content', 'utmContent', 200],
    ['utm_term', 'utmTerm', 200],
    ['lp_variant', 'lpVariant', 200],
  ];

  for (const [snake, camel, maxLen] of mapped) {
    const value = normalizeParam(params, snake, camel);
    if (!value) continue;
    safeParams.set(snake, safeValue(value, maxLen));
  }

  for (const key of ALLOWED_QUERY_PARAMS) {
    if (key.startsWith('utm_') || key === 'lp_variant') continue;
    const value = params.get(key);
    if (!value) continue;
    safeParams.set(key, safeValue(value, 500));
  }

  const query = safeParams.toString();
  return query ? `${pathname}?${query}` : pathname;
}

const PageTracking = () => {
    const location = useLocation();
    const lastTrackedRef = useRef<string | null>(null);

    useEffect(() => {
        // Push mm_page_view event on every route change
        // This handles both initial load and subsequent navigation
        pushMarketingEvent('mm_page_view', {});

        // Keep DB page view tracking production-only and privacy-safe.
        if (typeof window === 'undefined') return;
        if (!window.location.hostname.endsWith('moonmanifest.com')) return;

        // Pull persisted UTMs from marketingContext (stored in localStorage/cookie)
        // so we keep campaign/ad attribution through the funnel.
        const marketing = getMarketingContext();
        const utm = marketing?.last_touch || marketing?.first_touch;
        const munged: Record<string, string | null | undefined> = {
          utm_source: utm?.utm_source,
          utm_medium: utm?.utm_medium,
          utm_campaign: utm?.utm_campaign,
          utm_content: utm?.utm_content,
          utm_term: utm?.utm_term,
          fbclid: utm?.fbclid || marketing?.fbclid,
          msclkid: utm?.msclkid || marketing?.bclid,
          fbc: utm?.fbc || marketing?.fbc,
          fbp: utm?.fbp || marketing?.fbp,
          lp_variant: marketing?.lp_variant,
          eqid: marketing?.email_queue_id,
        };

        // Persist experiment variants into telemetry even when we do not want them in the visible URL.
        // This is required for the hero A/B/C test because assignment is stored in sessionStorage.
        try {
          munged.vsl_variant = sessionStorage.getItem('mm_vsl_cta_variant') || undefined;
          munged.hero_variant = sessionStorage.getItem('mm_hero_variant') || undefined;
          munged.hook_variant = sessionStorage.getItem('mm_vsl_hook_variant') || undefined;
        } catch (_) {
          // ignore
        }

        const pageUrl = sanitizePageUrl(location.pathname, location.search, munged);
        if (lastTrackedRef.current === pageUrl) return;
        lastTrackedRef.current = pageUrl;

        void fetch('/.netlify/functions/track-page-visit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pageUrl }),
          credentials: 'include',
          keepalive: true,
          referrerPolicy: 'no-referrer',
        }).catch(() => {
          // Tracking must never break navigation.
        });
    }, [location.pathname, location.search]);

    return null;
};

export default PageTracking;
